/* eslint-disable no-unsafe-optional-chaining */
import { makeAutoObservable } from 'mobx'

import { GroupInfo } from '../common'
import type { LessonClassWithStudentIdColl } from '../LessonClass/LessonClassWithStudentIdColl'
import { SchoolGrade } from './SchoolGrade'
import type { Student } from './Student'
import { StudentColl } from './StudentColl'

export type LessonClassGroup = {
  type: 'lesson-class'
  id: number
  name: string
  students: StudentColl
}
export type GradeGroup = {
  type: 'grade'
  id: (typeof SchoolGrade.KO_LABEL)[number]
  name: string
  students: StudentColl
}
export type StudentGroupT = LessonClassGroup | GradeGroup

export class StudentGroups {
  studentColl?: StudentColl
  lessonClassColl?: LessonClassWithStudentIdColl

  constructor() {
    makeAutoObservable(this)
  }

  static fromStudentColl(studentColl: StudentColl) {
    const studentGroups = new StudentGroups()
    studentGroups.setStudentColl(studentColl)
    return studentGroups
  }

  get isStudentEmpty() {
    return this.studentColl !== undefined && this.studentColl.size === 0
  }

  get hasStudent() {
    return !this.isStudentEmpty
  }

  // 학년 별 학생
  get studentsByGrade() {
    if (!this.studentColl) return
    // 학년들 순서대로 생성(초1 - 고3 순서 지키기 위함)
    const studentsByGradeColl = new Map<GradeGroup['id'], GradeGroup>(
      SchoolGrade.KO_LABEL.map((label) => [
        label,
        { type: 'grade', id: label, name: label, students: new StudentColl() },
      ]),
    )
    // 학생 순회하며 학년에 추가
    for (const student of this.studentColl.toArr) {
      const key = student.schoolGrade.koLabel
      studentsByGradeColl.get(key)?.students.add(student)
    }

    // 학생수 0인 학년 삭제
    // for (const [key, studentByGrade] of studentsByGradeColl) {
    //   if (studentByGrade.students.size === 0) studentsByGradeColl.delete(key)
    // }

    return studentsByGradeColl
  }

  // 반별 학생
  get studentsByLessonClass() {
    if (!this.studentColl || !this.lessonClassColl) return
    // 이름 순 정렬
    const mapEntry = this.lessonClassColl.toArr.map((lessonClass) => {
      const studentList = lessonClass.memberList
        .map(({ id }) => this.studentColl!.get(id)!)
        .filter((student) => !!student)
      const studentColl = new StudentColl(studentList)

      return [
        lessonClass.id,
        { type: 'lesson-class', id: lessonClass.id, name: lessonClass.name, students: studentColl },
      ] as [LessonClassGroup['id'], LessonClassGroup]
    })
    return new Map<LessonClassGroup['id'], LessonClassGroup>(mapEntry)
  }

  get gradeGroupArr() {
    if (!this.studentsByGrade) return []
    return [...this.studentsByGrade?.values()]
  }

  get lessonClassGroupArr() {
    if (!this.studentsByLessonClass) return []
    return [...this.studentsByLessonClass?.values()]
  }

  get allGroupArr() {
    return [...this.gradeGroupArr, ...this.lessonClassGroupArr]
  }

  // 기획측에서 정의한 노출되어야 하는 그룹 순서
  get firstGradeGroup() {
    if (!this.studentsByGrade) return

    const SORT_KEY = [
      '중1',
      '중2',
      '중3',
      '고1',
      '고2',
      '고3',
      '초6',
      '초5',
      '초4',
      '초3',
      '초2',
      '초1',
    ] as GradeGroup['id'][]

    for (const grade of SORT_KEY) {
      const targetGrade = this.studentsByGrade.get(grade)
      if (targetGrade && targetGrade.students.size > 0) return targetGrade
    }
  }

  get firstLessonClass() {
    return this.lessonClassGroupArr?.[0]
  }

  // groupInfo를 통해 특정 Group 가져오기
  getGroupFromGroupInfo(groupInfo: GroupInfo<'grade' | 'lesson-class'>) {
    switch (groupInfo.data.type) {
      case 'lesson-class':
        return this.studentsByLessonClass?.get(groupInfo.data.classId)
      case 'grade':
        return this.studentsByGrade?.get(groupInfo.data.schoolGrade.koLabel)
    }
  }

  getStudentFromGroupInfo(groupInfo: GroupInfo<'student'>) {
    return this.studentColl?.get(groupInfo.data.studentId)
  }

  // groupInfo나 StudentId를 통해 특정 Student 가져오기
  getStudent(groupInfo: GroupInfo<'student'>): Student
  getStudent(studentId: Student['id']): Student
  getStudent(arg: GroupInfo<'student'> | Student['id']) {
    return this.studentColl?.get(arg instanceof GroupInfo ? arg.data.studentId : arg)
  }

  setStudentColl(studentColl: StudentColl) {
    this.studentColl = studentColl
  }

  setLessonClassColl(lessonClassColl: LessonClassWithStudentIdColl) {
    this.lessonClassColl = lessonClassColl
  }
}
